import * as React from 'react';
import { FaSort, FaSortUp, FaSortDown, FaUndoAlt, FaTrash, FaMobileAlt, FaTabletAlt, FaDesktop, FaFileImport } from 'react-icons/fa';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';
import { Employee } from '../../../hooks/useEmployeeData';
import { TableSkeleton, timeTrackingColumns } from '../../../components/Skeleton';
import { toast } from 'react-toastify';

interface WorkArea {
  id: number;
  name: string;
  [key: string]: any;
}

interface BreakPeriod {
  id?: number;
  start_time: string;
  end_time: string;
  [key: string]: any;
}

interface TimeTracking {
  id: number;
  employee: number;
  work_area: number;
  work_area__name?: string;
  start_time: string;
  end_time: string;
  break_start?: string | null;
  break_end?: string | null;
  breakperiods?: BreakPeriod[];
  [key: string]: any;
}

type SortKey = 'employee' | 'workArea' | 'day' | 'source' | 'times' | 'break' | 'net' | 'planned' | 'sum';

interface TTTableProps {
  loading: boolean;
  selectedWAs: number[];
  timeTrackings: TimeTracking[];
  sortedTimeTrackings: TimeTracking[];
  displayLimit: number;
  setDisplayLimit: React.Dispatch<React.SetStateAction<number>>;
  selectedTTIds: Set<number>;
  setSelectedTTIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  sortBy: SortKey;
  sortDir: 'asc' | 'desc';
  toggleSort: (key: SortKey, isNumeric?: boolean) => void;
  employees: Employee[];
  workAreas: WorkArea[];
  plannedHoursMap: Record<number, number>;
  workedHoursMap: Record<number, number>;
  approvedCrMap: Record<number, { start: string; end: string }>;
  pendingCrMap: Record<number, { start: string; end: string }>;
  restoredOriginalMap: Record<number, { start: string; end: string }>;
  uiLedger: Record<number, { originalStart: string; originalEnd: string }>;
  crPillStyle: React.CSSProperties;
  formatDay: (iso: any) => string;
  formatTime: (iso: any) => string;
  calcBreak: (tt: any) => string;
  calcNetHoursNumber: (tt: TimeTracking) => number;
  normalizeUTC: (iso?: string | null) => string;
  toUTCIfNaiveLocal: (iso?: string | null) => string | undefined;
  handleEditTT: (tt: any) => void;
  handleDeleteTT: (ttId: any) => Promise<void>;
  openCRViewModal: (ttId: number) => Promise<void>;
  openCRApprovedModal: (ttId: number) => Promise<void>;
  handleReverseClick: (ttId: number) => Promise<void>;
}

export default function TTTable({
  loading,
  selectedWAs,
  timeTrackings,
  sortedTimeTrackings,
  displayLimit,
  setDisplayLimit,
  selectedTTIds,
  setSelectedTTIds,
  sortBy,
  sortDir,
  toggleSort,
  employees,
  plannedHoursMap,
  workedHoursMap,
  approvedCrMap,
  pendingCrMap,
  restoredOriginalMap,
  uiLedger,
  crPillStyle,
  formatDay,
  formatTime,
  calcBreak,
  calcNetHoursNumber,
  normalizeUTC,
  toUTCIfNaiveLocal,
  handleEditTT,
  handleDeleteTT,
  openCRViewModal,
  openCRApprovedModal,
  handleReverseClick,
}: TTTableProps): React.JSX.Element | null {

  const renderSortIcon = (key: SortKey): React.ReactNode => {
    const isActive = sortBy === key;
    const color = isActive ? colors.navy : colors.textLight;
    const style = { marginLeft: 6, verticalAlign: 'middle' } as React.CSSProperties;
    if (!isActive) return <FaSort style={style} color={color} />;
    return sortDir === 'asc' ? <FaSortUp style={style} color={color} /> : <FaSortDown style={style} color={color} />;
  };

  if (loading) return <div data-testid="loading" style={{ padding: '0 16px' }}><TableSkeleton columns={timeTrackingColumns} rows={10} /></div>;

  if (selectedWAs.length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '200px',
        color: colors.textMuted,
        fontSize: '14px',
      }}>
        <p>Bitte Work Area(s) auswählen, um Time Trackings anzuzeigen.</p>
      </div>
    );
  }

  if (timeTrackings.length === 0) return <p data-testid="no-tts">No TTs found.</p>;

  return (
    <div style={{
      background: colors.bgCard,
      borderRadius: '12px',
      border: `1px solid ${colors.border}`,
      boxShadow: shadows.card,
      overflow: 'hidden',
    }}>
    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px', tableLayout:'fixed' }}>
      <colgroup>
        <col style={{ width: '26px' }} />
        <col style={{ width: '115px' }} />
        <col style={{ width: '95px' }} />
        <col style={{ width: '80px' }} />
        <col style={{ width: '52px' }} />
        <col style={{ width: '130px' }} />
        <col style={{ width: '50px' }} />
        <col style={{ width: '45px' }} />
        <col style={{ width: '55px' }} />
        <col style={{ width: '42px' }} />
        <col style={{ width: '140px' }} />
      </colgroup>
      <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
        <tr style={{ borderBottom:'none' }}>
          <th style={{ padding:'8px 4px', textAlign:'left', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none' }}>
            {(() => {
              const displayedIds = sortedTimeTrackings.map(tt => tt.id);
              const allSelected = displayedIds.length>0 && displayedIds.every(id => selectedTTIds.has(id));
              const toggleAll = () => {
                setSelectedTTIds(prev => {
                  const next = new Set(prev);
                  if (allSelected) {
                    displayedIds.forEach(id => next.delete(id));
                  } else {
                    displayedIds.forEach(id => next.add(id));
                  }
                  return next;
                });
              };
              return (
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  onChange={toggleAll}
                />
              );
            })()}
          </th>
          <th style={{ padding:'8px 4px', textAlign:'left', cursor:'pointer', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none', whiteSpace:'nowrap' }} title="Nach Employee sortieren" aria-sort={sortBy==='employee'?(sortDir==='asc'?'ascending':'descending'):'none'} onClick={() => toggleSort('employee', false)}>Employee {renderSortIcon('employee')}</th>
          <th style={{ padding:'8px 4px', textAlign:'left', cursor:'pointer', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none', whiteSpace:'nowrap' }} title="Nach WorkArea sortieren" aria-sort={sortBy==='workArea'?(sortDir==='asc'?'ascending':'descending'):'none'} onClick={() => toggleSort('workArea', false)}>WorkArea {renderSortIcon('workArea')}</th>
          <th style={{ padding:'8px 4px', textAlign:'left', cursor:'pointer', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none', whiteSpace:'nowrap' }} title="Nach Tag sortieren" aria-sort={sortBy==='day'?(sortDir==='asc'?'ascending':'descending'):'none'} onClick={() => toggleSort('day', false)}>Day {renderSortIcon('day')}</th>
          <th style={{ padding:'8px 4px', textAlign:'center', cursor:'pointer', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none', whiteSpace:'nowrap' }} title="Quelle (App/Import/Station/Admin)" aria-sort={sortBy==='source'?(sortDir==='asc'?'ascending':'descending'):'none'} onClick={() => toggleSort('source', false)}>Source {renderSortIcon('source')}</th>
          <th style={{ padding:'8px 4px', textAlign:'left', cursor:'pointer', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none', whiteSpace:'nowrap' }} title="Nach Zeit sortieren" aria-sort={sortBy==='times'?(sortDir==='asc'?'ascending':'descending'):'none'} onClick={() => toggleSort('times', false)}>Times {renderSortIcon('times')}</th>
          <th style={{ padding:'8px 4px', textAlign:'left', cursor:'pointer', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none', whiteSpace:'nowrap' }} title="Nach Pause sortieren" aria-sort={sortBy==='break'?(sortDir==='asc'?'ascending':'descending'):'none'} onClick={() => toggleSort('break', true)}>Break {renderSortIcon('break')}</th>
          <th style={{ padding:'8px 4px', textAlign:'left', cursor:'pointer', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none', whiteSpace:'nowrap' }} title="Nach Nettozeit sortieren" aria-sort={sortBy==='net'?(sortDir==='asc'?'ascending':'descending'):'none'} onClick={() => toggleSort('net', true)}>Net {renderSortIcon('net')}</th>
          <th style={{ padding:'8px 4px', textAlign:'left', cursor:'pointer', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none', whiteSpace:'nowrap' }} title="Nach geplant sortieren" aria-sort={sortBy==='planned'?(sortDir==='asc'?'ascending':'descending'):'none'} onClick={() => toggleSort('planned', true)}>Planned {renderSortIcon('planned')}</th>
          <th style={{ padding:'8px 4px', textAlign:'left', cursor:'pointer', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none', whiteSpace:'nowrap' }} title="Nach Summe sortieren" aria-sort={sortBy==='sum'?(sortDir==='asc'?'ascending':'descending'):'none'} onClick={() => toggleSort('sum', true)}>Sum {renderSortIcon('sum')}</th>
          <th style={{ padding:'8px 4px', textAlign:'left', position:'sticky', top:0, background: colors.bgCard, zIndex:2, fontWeight:600, fontSize:'11px', color: colors.textBody, letterSpacing:'0.2px', border:'none', whiteSpace:'nowrap' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {sortedTimeTrackings.slice(0, displayLimit).map(tt => {
          const empObj= employees.find(e => e.id===tt.employee);
          const baseStart = approvedCrMap[tt.id]?.start || restoredOriginalMap[tt.id]?.start || tt.start_time;
          const baseEnd = approvedCrMap[tt.id]?.end || restoredOriginalMap[tt.id]?.end || tt.end_time;
          const crObj = (tt as any).active_change_request;
          const isPendingCR = (crObj?.status === 'PENDING') || Boolean(pendingCrMap[tt.id]);
          const usedPending = isPendingCR && !!pendingCrMap[tt.id];
          const usedApproved = !usedPending && !!approvedCrMap[tt.id];
          const usedRestored = !usedPending && !usedApproved && !!restoredOriginalMap[tt.id];
          const crStartRaw = (crObj as any)?.new_start_time || (crObj as any)?.requested_start_time || (crObj as any)?.proposed_start_time;
          const crEndRaw   = (crObj as any)?.new_end_time   || (crObj as any)?.requested_end_time   || (crObj as any)?.proposed_end_time;
          const dispStartRaw = isPendingCR ? (pendingCrMap[tt.id]?.start || crStartRaw || baseStart) : baseStart;
          const dispEndRaw   = isPendingCR ? (pendingCrMap[tt.id]?.end   || crEndRaw   || baseEnd)   : baseEnd;
          const normalizeBySource = (iso: string | null | undefined, fromTT?: boolean): string => {
            if (!iso) return '';
            if (fromTT) return normalizeUTC(iso);
            return toUTCIfNaiveLocal(iso) || normalizeUTC(iso);
          };
          const startNorm = normalizeBySource(dispStartRaw, !usedPending && !usedApproved && !usedRestored);
          const endNorm = normalizeBySource(dispEndRaw, !usedPending && !usedApproved && !usedRestored);
          const dayStr= formatDay(startNorm);
          const timesStr= `${formatTime(startNorm)} - ${formatTime(endNorm)}`;
          const brkStr= calcBreak(tt);
          const netHours = calcNetHoursNumber(tt);
          const netStr = netHours.toFixed(1) + 'h';
          const plannedStr = (plannedHoursMap[tt.employee] || 0).toFixed(1) + 'h';
          const worked = workedHoursMap[tt.employee] || 0;
          const sumStr = (worked + (plannedHoursMap[tt.employee] || 0)).toFixed(1) + 'h';
          const isChangedApproved = !isPendingCR && Boolean(approvedCrMap[tt.id] || (crObj?.status === 'APPROVED'));
          const isFallbackC = !!uiLedger[tt.id];
          const showChangedUI = isChangedApproved || isFallbackC;
          const isSel = selectedTTIds.has(tt.id);

          return (
            <tr
              key={tt.id}
              data-testid={`tt-row-${tt.id}`}
              className="tt-row"
              style={{ borderBottom: `1px solid ${colors.borderLight}`, cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <td style={{ padding:'6px' }}>
                <input
                  type="checkbox"
                  data-testid={`tt-select-${tt.id}`}
                  checked={isSel}
                  onChange={() => setSelectedTTIds(prev => {
                    const next = new Set(prev);
                    if (next.has(tt.id)) next.delete(tt.id); else next.add(tt.id);
                    return next;
                  })}
                />
              </td>
              <td data-testid={`tt-employee-${tt.id}`} style={{ padding:'6px 4px 6px 8px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                { (tt as any).employee_name }
              </td>
              <td data-testid={`tt-work-area-${tt.id}`} style={{ padding:'6px 8px 6px 4px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tt.work_area__name || ''}</td>
              <td data-testid={`tt-day-${tt.id}`} style={{ padding:'6px 0 6px 6px', marginRight:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{dayStr}</td>
              <td style={{ padding:'6px 6px 6px 0', width:60, textAlign:'center' }}>
                {(() => {
                  const srcRaw = String((tt as any).created_via || (tt as any).source_type || (tt as any).origin || '').toLowerCase();
                  let Icon: any = null; let title = '';
                  if (srcRaw === 'import_file') { Icon = FaFileImport; title = 'Timetracking via Import file'; }
                  else if (srcRaw === 'employee_app') { Icon = FaMobileAlt; title = 'Timetracking via Employee App'; }
                  else if (srcRaw === 'station_app') { Icon = FaTabletAlt; title = 'Timetracking via Station App'; }
                  else if (srcRaw === 'admin_desk') { Icon = FaDesktop; title = 'Timetracking via Admin Desk'; }
                  if (!Icon) return null;
                  return (
                    <span title={title} aria-label={title} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:20, height:20, color: colors.navy, margin:'0 auto', transform: srcRaw === 'station_app' ? 'rotate(90deg)' : undefined }}>
                      <Icon size={14} />
                    </span>
                  );
                })()}
              </td>
              <td data-testid={`tt-times-${tt.id}`} style={{ padding:'6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                <div style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    <span data-testid={`time-tracking-start-time-${tt.id}`}>{formatTime(startNorm)}</span>
                    {' - '}
                    <span data-testid={`time-tracking-end-time-${tt.id}`}>{formatTime(endNorm)}</span>
                  </span>
                {isPendingCR && (
                  <button
                    title="Change requested"
                    className="hover-back"
                    onClick={() => openCRViewModal(tt.id)}
                    style={{ ...crPillStyle, width: 'auto', borderRadius: 9999, padding: '0 8px' }}
                  >CR</button>
                )}
                  {showChangedUI && (
                  <>
                    <button
                        title={isFallbackC ? 'Change approved (via Fallback)' : 'Change approved'}
                      className="hover-back"
                      onClick={() => openCRApprovedModal(tt.id)}
                      style={crPillStyle}
                    >C</button>
                    <button
                      title="Reverse approval"
                      className="hover-back"
                      onClick={() => handleReverseClick(tt.id)}
                      style={crPillStyle}
                    >
                        <FaUndoAlt size={10} />
                    </button>
                  </>
                )}
                </div>
              </td>
              <td data-testid={`tt-break-${tt.id}`} style={{ padding:'6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{brkStr}</td>
              <td data-testid={`tt-net-${tt.id}`} style={{ padding:'6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{netStr}</td>
              <td data-testid={`tt-planned-${tt.id}`} style={{ padding:'6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{plannedStr}</td>
              <td data-testid={`tt-sum-${tt.id}`} style={{ padding:'6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                <span style={{ display:'inline-block', minWidth:0 }}>{sumStr}</span>
              </td>
              <td data-testid={`tt-actions-${tt.id}`} style={{ padding:'6px', overflow:'hidden' }}>
                <div style={{ display:'inline-flex', gap:4, alignItems:'center' }}>
                  <button
                    style={{
                      ...ps.buttonPrimary,
                      padding: '5px 10px',
                      fontSize: '11px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = shadows.medium;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = shadows.card;
                    }}
                    onClick={()=>handleEditTT(tt)}
                    data-testid={`edit-time-tracking-button-${tt.id}`}
                  >
                    Adjust
                  </button>
                  <button
                    style={{
                      background: colors.bgCard,
                      color: colors.success,
                      border: `1px solid ${colors.success}33`,
                      borderRadius: '8px',
                      padding: '5px 10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      boxShadow: shadows.soft,
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = colors.successLight;
                      e.currentTarget.style.borderColor = colors.success;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = colors.bgCard;
                      e.currentTarget.style.borderColor = `${colors.success}33`;
                    }}
                    onClick={()=>toast.success(`Approved #${tt.id}`)}
                  >
                    ✓
                  </button>
                  <button
                    style={{
                      background: colors.bgCard,
                      color: colors.danger,
                      border: `1px solid ${colors.danger}33`,
                      borderRadius: '8px',
                      width: 28,
                      height: 28,
                      padding: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: shadows.soft,
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = colors.dangerLight;
                      e.currentTarget.style.borderColor = colors.danger;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = colors.bgCard;
                      e.currentTarget.style.borderColor = `${colors.danger}33`;
                    }}
                    onClick={()=>handleDeleteTT(tt.id)}
                    title="Delete"
                    aria-label="Delete"
                    data-testid={`delete-time-tracking-button-${tt.id}`}
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    {sortedTimeTrackings.length > displayLimit && (
      <div style={{
        padding: '16px',
        textAlign: 'center',
        borderTop: `1px solid ${colors.borderLight}`,
        background: colors.bgMuted
      }}>
        <button
          onClick={() => setDisplayLimit(prev => prev + 50)}
          className="glass-btn glass-btn-secondary"
          style={{
            fontSize: '13px',
            padding: '10px 20px',
            borderRadius: '20px',
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.soft,
            color: colors.textBody,
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          Mehr laden ({sortedTimeTrackings.length - displayLimit} weitere)
        </button>
      </div>
    )}
    </div>
  );
}
